/**
 * Compact call transcript embedded into the ticket detail pane. Shows
 * the same dedup'd dialogue as the full CallTimelinePage but without
 * the horizontal pipeline view — just the conversation, plus a "open
 * full timeline" jump-link for power users.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Bot, ExternalLink, Hammer, Loader2, Mic, Phone } from 'lucide-react';
import { useCall, useCallEvents } from '../hooks/useCalls';
import { buildTranscript, fmtCallTs, type TranscriptTurn } from '../lib/buildTranscript';
import { cn } from '../lib/utils';

interface Props {
  callId: string;
  /** When true, hide the "open full timeline" jump link (e.g. when the
   *  panel is rendered inside a context that already provides one). */
  hideJumpLink?: boolean;
}

export function TicketCallLogPanel({ callId, hideJumpLink }: Props) {
  const call = useCall(callId);
  const events = useCallEvents(callId);
  const turns = React.useMemo(() => buildTranscript(events.events), [events.events]);

  if (events.loading && turns.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Anruf-Log …
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <CallMetaRow
        callerNumber={call.data?.callerNumber}
        startedAt={call.data?.startedAt?.toDate?.() ?? null}
        durationSec={call.data?.durationSec ?? 0}
        provider={call.data?.provider ?? null}
        latencyP50={call.data?.latencyP50 ?? null}
      />
      {turns.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          Kein Sprach-Content erfasst.
        </div>
      ) : (
        <div className="space-y-1.5">
          {turns.map((t) => (
            <TranscriptRow key={t.id} turn={t} />
          ))}
        </div>
      )}
      {!hideJumpLink && (
        <Link
          to={`/settings/calls/${callId}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
        >
          <ExternalLink className="size-3" />
          Vollständige Pipeline-Ansicht öffnen
        </Link>
      )}
    </div>
  );
}

function CallMetaRow({
  callerNumber,
  startedAt,
  durationSec,
  provider,
  latencyP50,
}: {
  callerNumber: string | undefined;
  startedAt: Date | null;
  durationSec: number;
  provider: string | null;
  latencyP50: number | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Phone className="size-3" />
        <span className="text-foreground">{callerNumber ?? '—'}</span>
      </span>
      {startedAt && (
        <span className="text-muted-foreground">
          {startedAt.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
        </span>
      )}
      <span className="text-muted-foreground">{durationSec} s</span>
      {provider && <span className="text-muted-foreground">· {provider}</span>}
      {latencyP50 != null && (
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-secondary-foreground">
          Latenz {latencyP50} ms
        </span>
      )}
    </div>
  );
}

function TranscriptRow({ turn }: { turn: TranscriptTurn }) {
  const isUser = turn.speaker === 'user';
  const isAssist = turn.speaker === 'assistant';
  const isTool = turn.speaker === 'tool';
  return (
    <div
      className={cn(
        'flex gap-2.5 rounded-md border px-2.5 py-1.5 text-sm',
        isUser && 'border-sky-500/30 bg-sky-500/5',
        isAssist && 'border-primary/30 bg-primary/5',
        isTool && 'border-amber-500/30 bg-amber-500/5',
      )}
    >
      <div className="flex shrink-0 flex-col items-center gap-0.5 pt-0.5">
        {isUser && <Mic className="size-3.5 text-sky-400" />}
        {isAssist && <Bot className="size-3.5 text-primary" />}
        {isTool && <Hammer className="size-3.5 text-amber-400" />}
        <span className="font-mono text-[9px] tabular-nums text-muted-foreground">
          {fmtCallTs(turn.ts)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'whitespace-pre-wrap break-words text-[13px] leading-snug',
            turn.textMissing && 'italic text-muted-foreground',
          )}
        >
          {turn.text}
        </div>
        {turn.meta && (
          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {turn.meta}
          </div>
        )}
      </div>
    </div>
  );
}
